import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FiAlertCircle, FiInfo, FiCheckCircle, FiPlus, FiSettings, FiX, FiZap } from 'react-icons/fi';
import { Node, Edge } from 'reactflow';
import { ConnectionSuggestion, generateIntermediateResource } from '../../utils/connectionSuggestions';

interface ConnectionSuggestionsDrawerProps {
  isOpen: boolean;
  sourceNode: Node;
  targetNode: Node;
  suggestions: ConnectionSuggestion[];
  onCreateResource?: (data: { node: Node; edges: Edge[] }) => void;
  onClose: () => void;
}

/**
 * Drawer de suggestions de connexion
 * S'ouvre depuis la droite pour afficher les suggestions lors de la création de connexions
 */
export const ConnectionSuggestionsDrawer: React.FC<ConnectionSuggestionsDrawerProps> = ({
  isOpen,
  sourceNode,
  targetNode,
  suggestions,
  onCreateResource,
  onClose,
}) => {
  const { t } = useTranslation('canvas');
  const [expandedSuggestions, setExpandedSuggestions] = useState<Set<string>>(new Set());

  const toggleSuggestion = (id: string) => {
    setExpandedSuggestions(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleCreateResource = (suggestion: ConnectionSuggestion) => {
    if (!onCreateResource) return;

    // Calculate position between source and target
    const midX = (sourceNode.position.x + targetNode.position.x) / 2;
    const midY = (sourceNode.position.y + targetNode.position.y) / 2;

    const newNode = generateIntermediateResource(
      suggestion,
      sourceNode,
      targetNode,
      { x: midX, y: midY }
    );

    if (newNode) {
      // Create edges to connect: source -> newNode -> target
      const edges: Edge[] = [
        {
          id: `${sourceNode.id}-${newNode.id}`,
          source: sourceNode.id,
          target: newNode.id,
          type: 'smoothstep',
          animated: false,
        },
        {
          id: `${newNode.id}-${targetNode.id}`,
          source: newNode.id,
          target: targetNode.id,
          type: 'smoothstep',
          animated: false,
        },
      ];

      onCreateResource({ node: newNode, edges });
      onClose();
    }
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'critical':
        return <FiAlertCircle size={18} style={{ color: '#EF4444' }} />;
      case 'recommended':
        return <FiInfo size={18} style={{ color: '#F59E0B' }} />;
      case 'optional':
        return <FiCheckCircle size={18} style={{ color: '#3B82F6' }} />;
      default:
        return <FiInfo size={18} style={{ color: '#6B7280' }} />;
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical':
        return { bg: '#FEE2E2', border: '#EF4444', text: '#991B1B' };
      case 'recommended':
        return { bg: '#FEF3C7', border: '#F59E0B', text: '#92400E' };
      case 'optional':
        return { bg: '#DBEAFE', border: '#3B82F6', text: '#1E3A8A' };
      default:
        return { bg: '#F3F4F6', border: '#9CA3AF', text: '#374151' };
    }
  };

  const criticalCount = suggestions.filter(s => s.severity === 'critical').length;
  const recommendedCount = suggestions.filter(s => s.severity === 'recommended').length;

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.3)',
          zIndex: 1999,
          backdropFilter: 'blur(2px)',
        }}
        onClick={onClose}
      />

      {/* Drawer */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          width: '450px',
          maxWidth: '90vw',
          background: '#FFFFFF',
          zIndex: 2000,
          boxShadow: '-4px 0 20px rgba(0, 0, 0, 0.15)',
          display: 'flex',
          flexDirection: 'column',
          animation: 'slideInRight 0.3s ease-out',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '20px 24px',
            borderBottom: '1px solid #E5E7EB',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <FiZap size={24} />
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>
                {t('connectionSuggestions.drawer.title')}
              </h3>
            </div>
            <button
              onClick={onClose}
              style={{
                background: 'rgba(255, 255, 255, 0.2)',
                border: 'none',
                cursor: 'pointer',
                padding: 8,
                display: 'flex',
                alignItems: 'center',
                borderRadius: 6,
                transition: 'background 0.2s',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)')}
              title={t('connectionSuggestions.drawer.close')}
            >
              <FiX size={20} style={{ color: 'white' }} />
            </button>
          </div>

          <div style={{ fontSize: 13, opacity: 0.95, marginBottom: 12 }}>
            <strong>{sourceNode.data.label || sourceNode.data.type}</strong>
            {' → '}
            <strong>{targetNode.data.label || targetNode.data.type}</strong>
          </div>

          {/* Stats badges */}
          <div style={{ display: 'flex', gap: 10 }}>
            {criticalCount > 0 && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '4px 10px',
                  background: 'rgba(239, 68, 68, 0.2)',
                  borderRadius: 12,
                  fontSize: 12,
                  fontWeight: 600,
                }}
              >
                <FiAlertCircle size={14} />
                <span>{t('connectionSuggestions.drawer.stats.critical', { count: criticalCount })}</span>
              </div>
            )}
            {recommendedCount > 0 && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '4px 10px',
                  background: 'rgba(245, 158, 11, 0.2)',
                  borderRadius: 12,
                  fontSize: 12,
                  fontWeight: 600,
                }}
              >
                <FiInfo size={14} />
                <span>{t('connectionSuggestions.drawer.stats.recommended', { count: recommendedCount })}</span>
              </div>
            )}
            {suggestions.length > 0 && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '4px 10px',
                  background: 'rgba(255, 255, 255, 0.2)',
                  borderRadius: 12,
                  fontSize: 12,
                  fontWeight: 600,
                }}
              >
                <span>{t('connectionSuggestions.drawer.stats.total', { count: suggestions.length })}</span>
              </div>
            )}
          </div>
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 24 }}>
          {suggestions.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 20px' }}>
              <div
                style={{
                  width: 80,
                  height: 80,
                  margin: '0 auto 20px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <FiCheckCircle size={40} style={{ color: 'white' }} />
              </div>
              <h4 style={{ fontSize: 16, fontWeight: 600, color: '#111827', marginBottom: 8 }}>
                {t('connectionSuggestions.drawer.noSuggestions.title')}
              </h4>
              <p style={{ fontSize: 14, color: '#6B7280', margin: 0 }}>
                {t('connectionSuggestions.drawer.noSuggestions.message')}
              </p>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: 16 }}>
              {suggestions.map((suggestion) => {
                const isExpanded = expandedSuggestions.has(suggestion.id);
                const colors = getSeverityColor(suggestion.severity);

                return (
                  <div
                    key={suggestion.id}
                    style={{
                      border: `1px solid ${colors.border}`,
                      borderLeft: `4px solid ${colors.border}`,
                      borderRadius: 8,
                      background: 'white',
                      overflow: 'hidden',
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                      transition: 'transform 0.2s, box-shadow 0.2s',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.05)';
                    }}
                  >
                    <div
                      onClick={() => toggleSuggestion(suggestion.id)}
                      style={{
                        padding: 16,
                        cursor: 'pointer',
                        background: colors.bg,
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                        <div style={{ flexShrink: 0, marginTop: 2 }}>
                          {getSeverityIcon(suggestion.severity)}
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                            {suggestion.icon && <span style={{ fontSize: 20 }}>{suggestion.icon}</span>}
                            <h4 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: colors.text }}>
                              {suggestion.title}
                            </h4>
                          </div>
                          <p style={{ margin: 0, fontSize: 13, color: '#6B7280', lineHeight: 1.6 }}>
                            {suggestion.description}
                          </p>
                        </div>
                        <div
                          style={{
                            flexShrink: 0,
                            fontSize: 12,
                            color: '#9CA3AF',
                            transition: 'transform 0.2s',
                            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                          }}
                        >
                          ▼
                        </div>
                      </div>
                    </div>

                    {isExpanded && (
                      <div style={{ padding: 16, borderTop: `1px solid ${colors.border}`, background: '#FAFAFA' }}>
                        {suggestion.type === 'create_resource' && suggestion.resourceType && (
                          <div>
                            <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 12 }}>
                              <strong>{t('connectionSuggestions.drawer.suggestion.resourceType')}</strong>
                              <code
                                style={{
                                  display: 'block',
                                  background: '#F3F4F6',
                                  padding: '6px 10px',
                                  borderRadius: 4,
                                  fontSize: 11,
                                  marginTop: 6,
                                  fontFamily: 'monospace',
                                }}
                              >
                                {suggestion.resourceType}
                              </code>
                            </div>
                            {suggestion.properties && Object.keys(suggestion.properties).length > 0 && (
                              <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 16 }}>
                                <strong>{t('connectionSuggestions.drawer.suggestion.properties')}</strong>
                                <pre
                                  style={{
                                    background: '#F9FAFB',
                                    padding: 10,
                                    borderRadius: 4,
                                    fontSize: 11,
                                    marginTop: 6,
                                    overflow: 'auto',
                                    maxHeight: 200,
                                    fontFamily: 'monospace',
                                    border: '1px solid #E5E7EB',
                                  }}
                                >
                                  {JSON.stringify(suggestion.properties, null, 2)}
                                </pre>
                              </div>
                            )}
                            {onCreateResource && (
                              <button
                                onClick={() => handleCreateResource(suggestion)}
                                style={{
                                  width: '100%',
                                  padding: '10px 16px',
                                  background: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: 6,
                                  cursor: 'pointer',
                                  fontSize: 14,
                                  fontWeight: 600,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  gap: 8,
                                  transition: 'transform 0.2s, box-shadow 0.2s',
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.transform = 'translateY(-1px)';
                                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(37, 99, 235, 0.3)';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.transform = 'translateY(0)';
                                  e.currentTarget.style.boxShadow = 'none';
                                }}
                              >
                                <FiPlus size={16} />
                                {t('connectionSuggestions.drawer.suggestion.createButton')}
                              </button>
                            )}
                          </div>
                        )}

                        {suggestion.type === 'configure_property' && (
                          <div>
                            <div
                              style={{
                                fontSize: 12,
                                color: '#6B7280',
                                marginBottom: 12,
                                padding: 10,
                                background: '#FEF3C7',
                                borderRadius: 6,
                                borderLeft: '3px solid #F59E0B',
                              }}
                            >
                              {t('connectionSuggestions.drawer.suggestion.manualWarning')}
                            </div>
                            <button
                              style={{
                                width: '100%',
                                padding: '10px 16px',
                                background: '#F3F4F6',
                                color: '#374151',
                                border: '1px solid #D1D5DB',
                                borderRadius: 6,
                                cursor: 'pointer',
                                fontSize: 14,
                                fontWeight: 500,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 8,
                                transition: 'background 0.2s',
                              }}
                              onMouseEnter={(e) => (e.currentTarget.style.background = '#E5E7EB')}
                              onMouseLeave={(e) => (e.currentTarget.style.background = '#F3F4F6')}
                            >
                              <FiSettings size={16} />
                              {t('connectionSuggestions.drawer.suggestion.configureManually')}
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Animation */}
      <style>{`
        @keyframes slideInRight {
          from {
            transform: translateX(100%);
          }
          to {
            transform: translateX(0);
          }
        }
      `}</style>
    </>
  );
};

export default ConnectionSuggestionsDrawer;
